import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, FilmStrip, CaretRight } from "@phosphor-icons/react";
import { api, fileUrl, avatarUrl } from "@/lib/api";
import logger from "@/lib/logger";

/**
 * Facebook-style horizontal "Reels" rail.
 *
 * Renders a row of swipe-able short-video tiles inside the regular feed,
 * inviting the user to tap into the fullscreen Reels viewer. We fetch from
 * `/api/reels` and only render if the API actually returns videos so the
 * rail never appears as an empty placeholder.
 */
export default function ReelsRail() {
  const [reels, setReels] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/reels?limit=12");
        if (!cancelled) setReels(Array.isArray(data) ? data : []);
      } catch (err) {
        logger.warn("reels-rail", "failed to load", err);
        if (!cancelled) setReels([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!reels || reels.length === 0) return null;

  return (
    <section data-testid="reels-rail" className="py-3 border-t border-b border-border/40 bg-secondary/20 mb-3">
      <div className="px-4 flex items-center justify-between mb-2">
        <h3 className="flex items-center gap-2 text-sm font-bold tracking-wide uppercase text-foreground/80">
          <FilmStrip size={18} weight="fill" className="text-primary" /> Reels for you
        </h3>
        <Link to="/reels" data-testid="reels-rail-see-all" className="flex items-center gap-0.5 text-xs text-primary font-semibold hover:underline">
          See all <CaretRight size={12} weight="bold" />
        </Link>
      </div>
      <div className="flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar snap-x snap-mandatory">
        {reels.map((r) => (
          <ReelTile key={r.post_id} reel={r} />
        ))}
      </div>
    </section>
  );
}

function ReelTile({ reel }) {
  const mediaSrc = reel.media_path ? fileUrl(reel.media_path) : null;
  return (
    <Link
      to={`/reels?start=${encodeURIComponent(reel.post_id)}`}
      data-testid={`reels-rail-tile-${reel.post_id}`}
      className="shrink-0 w-32 sm:w-36 aspect-[9/16] rounded-xl overflow-hidden relative snap-start group bg-black"
    >
      {mediaSrc && (
        <video
          src={mediaSrc}
          muted
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0" />
      <div className="absolute top-2 left-2">
        <Play size={16} weight="fill" className="text-white drop-shadow" />
      </div>
      <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5">
        <img
          src={avatarUrl(reel.user_picture)}
          alt=""
          className="h-5 w-5 rounded-full object-cover ring-1 ring-white/60"
        />
        <span className="text-[11px] text-white font-semibold truncate drop-shadow">
          {reel.user_name || "User"}
        </span>
      </div>
    </Link>
  );
}
